import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import { createLoadingSelector, showModal, hideModal } from 'erisxkit/client';
import get from 'lodash/get';
import EMPTable8 from '../../common/EMPTable8/EMPTable8';
import { CAR } from '../../ts/models/CAR.model';
import {
  getCarList,
  fetchCarList,
  updateCarPromiseCreator,
  deleteCarPromiseCreator,
} from '../../reducers/CARManagement/carManagementReducer';
import {
  GET_CUSTOMER_ACCOUNT_REFERENCE_LIST,
  DELETE_CUSTOMER_ACCOUNT_REFERENCE,
} from '../../reducers/CARManagement/carManagementActions';
import { ADD_NEW_CAR } from '../../constants/modalTypes';
import Page from '../../common/Page';
import { TableEditState } from '../../common/EMPTable8/types';
import { CarOption } from '../../ts/models/CAROption.model';
import { preTradeRiskColumns } from '../../metadata/preTradeRiskMetadata';
import PreTradeRiskActions, { SELECT_CAR_FORM } from './PreTradeRiskActions';
import CAR_EDIT_DISCLAIMER from './contants';
import {
  fetchCgmList,
  getCarTableCgmSelectOptions,
} from '../../reducers/CGMManagement/cgmManagementReducer';
import { getActiveOrFirstAccountLabel } from '../../reducers/activeAccountSelectors';
import { Row } from '@tanstack/react-table';
import { ADD_NEW_CGM, GENERIC_MODAL } from '../../constants/modalTypes';
import { StyledSelectOption } from '../../common/StyledSelect';
import { GenericModalProps } from '../../common/components/GenericModal';
import colors from '../../constants/colors';
import { getLoggedInUseCanWritePreTradeRisk } from '../../reducers/userReducer';
import AccountSelectorFCM from '../AccountSelector';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { RootState } from '../../ts/types/store';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PreTradeRisk = (props: { location: { search: string } }) => {
  const firstRender = useRef(true);
  const dispatch = useDispatch();
  const carsFromSelector: CAR[] = useSelector(getCarList);
  const accountLabel: string = useSelector(getActiveOrFirstAccountLabel);
  const cgms: StyledSelectOption[] = useSelector(getCarTableCgmSelectOptions);

  const canWrite = useSelector(getLoggedInUseCanWritePreTradeRisk);

  const options = useMemo(
    () => [
      {
        key: 'addCgm',
        value: '',
        text: '+ Add CGM',
        onClick: () => {
          dispatch(showModal(ADD_NEW_CGM, { size: 'large' }));
        },
      },
      ...cgms,
    ],
    [cgms, dispatch],
  );
  const formValues = useSelector((state) =>
    getFormValues(SELECT_CAR_FORM)(state),
  );

  const loading = useSelector((state) =>
    createLoadingSelector([GET_CUSTOMER_ACCOUNT_REFERENCE_LIST])(state),
  );
  const selectedCAR: CarOption = get(formValues, 'selectedCAR', null);

  const memoizedFetch = useCallback(() => {
    dispatch(
      fetchCarList({
        urlParams: { accountLabel },
        queryParams: {
          isTme: false,
        },
      }),
    );
    dispatch(
      fetchCgmList({
        urlParams: { accountLabel },
        queryParams: {
          isTme: false,
        },
      }),
    );
  }, [accountLabel, dispatch]);

  useEffect(() => {
    if (!firstRender.current && accountLabel) {
      memoizedFetch();
    } else {
      firstRender.current = false;
    }
  }, [accountLabel, dispatch, memoizedFetch]);

  const memoizedSave = useCallback(
    (changes: TableEditState<Partial<CAR>>) => {
      const customerAccountNumber = get(
        changes,
        'updated.customerAccountNumber',
        '',
      );
      updateCarPromiseCreator(
        {
          urlParams: {
            accountLabel,
            customerAccountNumber,
          },
          ...changes.updated,
          isTme: false,
        },
        dispatch,
      ).then(() =>
        dispatch(
          fetchCarList({
            urlParams: {
              accountLabel,
            },
            queryParams: {
              isTme: false,
            },
          }),
        ),
      );
    },
    [accountLabel, dispatch],
  );

  const memoizedColumns = useMemo(() => {
    return preTradeRiskColumns(options, canWrite);
  }, [options, canWrite]);

  const onCloneClicked = useCallback(
    (row: Row<CAR>) => {
      const data = get(row, 'original', null);
      dispatch(showModal(ADD_NEW_CAR, { size: 'huge', data }));
    },
    [dispatch],
  );

  const memoizedDelete = useCallback(
    async (customerAccountNumber) => {
      try {
        await deleteCarPromiseCreator(
          {
            urlParams: {
              accountLabel,
              customerAccountNumber,
            },
            queryParams: {
              isTme: false,
            },
          },
          dispatch,
        );
        dispatch(hideModal());
        dispatch(
          fetchCarList({
            urlParams: {
              accountLabel,
            },
            queryParams: {
              isTme: false,
            },
          }),
        );
      } catch (error) {
        dispatch(hideModal());
      }
    },
    [accountLabel, dispatch],
  );

  const onDeleteClicked = useCallback(
    (row: Row<CAR>) => {
      const data = get(row, 'original', null);
      dispatch(
        showModal(GENERIC_MODAL, {
          header: 'Confirm Delete',
          content: `Are you sure you want to remove CAR: ${data?.customerAccountNumber}?`,
          cancelProps: {
            text: 'Cancel',
            onClick: () => dispatch(hideModal()),
          },
          confirmProps: {
            text: 'Delete',
            onClick: () => {
              memoizedDelete(data?.customerAccountNumber);
            },
            danger: true,
          },
          loadingSelector: (state: RootState) =>
            createLoadingSelector([DELETE_CUSTOMER_ACCOUNT_REFERENCE])(state),
        } as GenericModalProps),
      );
    },
    [dispatch, memoizedDelete],
  );

  const initialState = useMemo(() => {
    const urlParams = new URLSearchParams(
      get(props, ['location', 'search'], ''),
    );
    const car = urlParams.get('car');
    const initialFilter = car
      ? [
          {
            id: 'customerAccountNumber',
            value: car,
          },
        ]
      : [];

    return {
      sorting: [{ id: 'customerAccountNumber', desc: false }],
      columnFilters: initialFilter,
    };
  }, [props]);

  return (
    <>
      <Page header="Customer Accounts">
        <Header>
          <AccountSelectorFCM />
          <PreTradeRiskActions />
        </Header>
        <EMPTable8
          title="CarTable"
          data={carsFromSelector}
          local
          columns={memoizedColumns}
          fetchData={memoizedFetch}
          count={carsFromSelector?.length}
          onSaveChanges={memoizedSave}
          editDisclaimer={CAR_EDIT_DISCLAIMER}
          loading={loading}
          loadingText="Fetching CAR"
          noDataText={
            selectedCAR
              ? 'No CAR could be found'
              : 'Select a Customer Account Ref'
          }
          minimumRows={5}
          showActions
          showActiveFilters
          additionalActions={(row: Row<CAR>) => [
            {
              text: 'Delete',
              onClick: () => onDeleteClicked(row),
              fontSize: '14',
              color: colors.RED_ERROR,
              disabled: !canWrite,
            },
            {
              text: 'Clone',
              onClick: () => onCloneClicked(row),
              fontSize: '14',
              color: colors.MEDIUM_NAVY,
              disabled: !canWrite,
            },
          ]}
          initialState={initialState}
        />
      </Page>
    </>
  );
};

export default withRouter(PreTradeRisk);
